import React from 'react';
import { type WidgetType } from 'src/components/card-widget/types';
import { Currency } from 'src/components/currency';
import { Logo } from 'src/components/logo';
import { Text } from 'src/components/text/Text';
import { INSTALLMENT_PLANS } from 'src/constants/learn-more';
import { useAppContext } from 'src/context/store/app-provider';
import { useGetSubvention } from 'src/hooks/use-get-subvention';
import { useLocale } from 'src/hooks/use-locale';
import { useNextBillingDate } from 'src/hooks/use-next-billing-date';
import { createNormalizeCheckingNumber } from 'src/utils/create-normalize-checking-number';
import { calculatePriceWithFees } from 'src/utils/default-fees-calculator';
import { getFormattedAmount } from 'src/utils/get-formatted-amount';
import { getPriceSplitOf } from 'src/utils/get-price-split-of';

import styles from './PaymentOptionSummary.css';

interface Props {
  widgetType: WidgetType;
}

const LIST_FOR_INTRO_TEXT = Array<WidgetType>(
  'pi4-ltf',
  'subvention',
  'ltf-ksa'
);

const LIST_FOR_HIDE_LOGO = Array<WidgetType>('ltf-ksa');

export const PaymentOptionSummary = ({ widgetType }: Props) => {
  const t = useLocale();
  const {
    currency,
    price,
    installmentsCount,
    offers: { installments: offersInstallments = [] },
    priceRaw,
  } = useAppContext();
  const preparedPrice = createNormalizeCheckingNumber(
    'Price argument is not a number',
    0
  )(priceRaw);
  const subvention = useGetSubvention();
  const billingDate = useNextBillingDate();
  const shouldShownIntroText = LIST_FOR_INTRO_TEXT.includes(widgetType);
  const shouldShowLogo = !LIST_FOR_HIDE_LOGO.includes(widgetType);
  const longestOfferPaymentsCount = Math.max(
    0,
    ...offersInstallments.map((offer) => offer.paymentsCount)
  );

  const getTitle = (type: WidgetType) => {
    switch (type) {
      case 'pi4-ltf': {
        const sortedPlansByAmount = offersInstallments
          .slice()
          .sort((a, b) => a.amount - b.amount);
        const cheapestInstallmentPlan = sortedPlansByAmount[0];

        const priceFormated = cheapestInstallmentPlan?.amount
          ? getFormattedAmount(cheapestInstallmentPlan.amount, currency)
          : getPriceSplitOf(
              calculatePriceWithFees({
                price: preparedPrice,
                installmentCount: INSTALLMENT_PLANS.twelve,
              }),
              currency,
              INSTALLMENT_PLANS.twelve
            );
        return t(
          'pay_per_month',
          {
            price: priceFormated,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }
      case 'pi4': {
        const sortedPlansByAmount = offersInstallments
          .slice()
          .sort((a, b) => a.amount - b.amount);
        const cheapestInstallmentPlan = sortedPlansByAmount[0];

        const priceFormated = cheapestInstallmentPlan?.amount
          ? getFormattedAmount(cheapestInstallmentPlan.amount, currency)
          : getPriceSplitOf(price, currency, INSTALLMENT_PLANS.four);

        return t(
          'pay_per_month',
          {
            price: priceFormated,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }
      case 'subvention':
        return t(
          'pay_per_month',
          {
            price: subvention.longestSubventionPlan?.amount,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      case 'ltf-ksa': {
        const sortedPlansByInstallmentsCount = offersInstallments
          .slice()
          .sort((a, b) => b.paymentsCount - a.paymentsCount);
        const longestPlan = sortedPlansByInstallmentsCount[0];
        const rawLtfKsaPrice = longestPlan?.amount || preparedPrice;

        if (!rawLtfKsaPrice) {
          return t('split_purchase');
        }

        const ltfKsaPrice = longestPlan?.amount
          ? getFormattedAmount(longestPlan.amount, currency)
          : getPriceSplitOf(preparedPrice, currency, INSTALLMENT_PLANS.twelve);

        return t(
          'pay_per_month',
          {
            price: ltfKsaPrice,
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      }
      case 'pay-later':
        return t('pay_next_month');
      case 'pay-in-full':
        return t('default.pay_in_full');
      default:
        return t('split_purchase');
    }
  };

  const getSubtitle = (type: WidgetType) => {
    switch (type) {
      case 'pi4-ltf':
        return t('or_interest_free', {
          installmentsCount,
        });
      case 'pi4':
        return t('interest_free_payments', {
          installmentsCount,
        });
      case 'subvention':
        return t('or_interest_free', {
          installmentsCount: subvention.longestSubventionPlan?.paymentsCount,
        });
      case 'ltf-ksa': {
        const paymentsCount =
          longestOfferPaymentsCount || INSTALLMENT_PLANS.twelve;

        return paymentsCount < 11
          ? t('monthly_payments_few', { installmentsCount: paymentsCount })
          : t('monthly_payments_other', { installmentsCount: paymentsCount });
      }
      case 'pay-later':
        return billingDate
          ? t(
              'pay_next_month_on_date',
              {
                price: getFormattedAmount(price, currency, true),
                date: `${billingDate.month} ${billingDate.day}`,
              },
              {
                currency: () => <Currency currency={currency} />,
              }
            )
          : null;
      case 'pay-in-full':
        return t(
          'pay_in_full',
          {
            price: getFormattedAmount(price, currency, true),
          },
          {
            currency: () => <Currency currency={currency} />,
          }
        );
      default:
        return t('into_monthly_payments');
    }
  };

  return (
    <div className={styles.PaymentOptionSummary}>
      {shouldShowLogo && (
        <Logo
          className={styles.PaymentOptionSummary__Logo}
          color="black on green"
        />
      )}
      <div className={styles.PaymentOptionSummary__Header}>
        {shouldShownIntroText && (
          <Text
            as="p"
            variant="caption"
            className={styles.PaymentOptionSummary__Intro}
            data-test="card-widget-intro-text"
          >
            {t('as_low_as')}
          </Text>
        )}
        <Text
          as="p"
          variant="h4"
          className={styles.PaymentOptionSummary__Title}
          data-test="card-widget-title"
        >
          {getTitle(widgetType)}
        </Text>
        <Text
          as="p"
          variant="caption"
          className={styles.PaymentOptionSummary__Description}
          data-test="card-widget-subtitle"
        >
          {getSubtitle(widgetType)}
        </Text>
      </div>
      <div className={styles.PaymentOptionSummary__Button}>
        <Text as="span" variant="caption" data-test="card-widget-button">
          {t('view_options')}
        </Text>
      </div>
    </div>
  );
};
